/**
 * Persona Configuration
 *
 * Defines the available personas and their associated icons.
 * This is a placeholder until the actual persona system is fully integrated.
 */

import FloorplanIcon from '../assets/icons/floorplan.svg?react';
import RoomIcon from '../assets/icons/Room.svg?react';
import Floor from '../assets/icons/Floor.svg?react';
import RulerIcon from '../assets/icons/ruler.svg?react';
import SurfaceIcon from '../assets/icons/surface.svg?react';
import PlaneIcon from '../assets/icons/plane.svg?react';
import WallIcon from '../assets/icons/wall.svg?react';

export interface PersonaModeConfig {
  id: string;
  /** Display name (plain English, used as translation key) */
  name: string;
  icon?: React.FC;
  /** Tooltip shown when tab is disabled (plain English, used as translation key) */
  disabledTooltip?: string;
}

export interface PersonaConfig {
  id: string;
  /** Display name (plain English, used as translation key) */
  name: string;
  icon: React.FC;
  /** Disabled in 2D mode */
  disabled2D?: boolean;
  /** Optional modes for this persona */
  modes?: PersonaModeConfig[];
  /** Not offered to read-only viewers (invited guests, share-link visitors): nothing finished to look at yet. */
  hiddenInReadOnly?: boolean;
  /** Mode read-only viewers are pinned to: the persona's end-result view (no mode tabs for them). */
  readOnlyModeId?: string;
}

/**
 * Available personas, in header-tab order:
 * - Monitoring: All measurement and analysis tools (Selection, Point, Line, Polygon, Box, Cluster, Plane)
 * - Floorplan: Floor plan generation and design (floors, rooms, walls, openings, objects)
 * - Surface: Surface detection and creation tools
 *
 * Note: All names are plain English strings used as translation keys.
 */
export const PERSONAS = [
  {
    id: 'monitoring' as const,
    name: 'monitoring',
    icon: RulerIcon,
    modes: [
      {
        id: 'default',
        name: 'monitoring',
        icon: RulerIcon,
      },
    ],
  },
  {
    id: 'floorplan' as const,
    name: 'floorplan',
    icon: FloorplanIcon,
    modes: [
      {
        id: 'floor_select',
        name: 'floor',
        icon: Floor,
      },
      {
        id: 'floorplan',
        name: 'room',
        icon: RoomIcon,
        disabledTooltip: 'create floor first',
      },
      {
        id: 'wall',
        name: 'wall',
        icon: WallIcon,
        disabledTooltip: 'create floor first',
      },
      {
        id: 'design',
        name: 'design',
        icon: SurfaceIcon,
        disabledTooltip: 'create floor first',
      },
    ],
    readOnlyModeId: 'design',
  },
  {
    id: 'surface' as const,
    name: 'surface',
    icon: PlaneIcon,
    hiddenInReadOnly: true,
    modes: [
      {
        id: 'default',
        name: 'surface',
        icon: PlaneIcon,
      },
    ],
  },
] satisfies PersonaConfig[];

/**
 * Union type of all valid persona IDs
 * Extracted from the PERSONAS array for type safety and autocomplete
 */
export type PersonaId = (typeof PERSONAS)[number]['id'];
